import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { DataPerformanceSummaryType, EmblemType } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import type { TeamSectionItem } from 'webapp/models-custom';
import type BatchSelect from 'webapp/models-custom/batch-select';
import type Goal from 'webapp/models-custom/goal';

import type { Action as TeamAction } from 'webapp/services/team';

export interface Args {
	model: Goal;
	batchSelect?: BatchSelect<Goal>;
	hideSort?: boolean;
	teamSectionItem?: TeamSectionItem;
	removeFromSection?: (item: TeamSectionItem) => void;
	isRestricted?: boolean;
	checkVisibility?: boolean;
	hideDragHandle?: boolean;
}

export type Action =
	| 'viewDetails'
	| 'edit'
	| 'updateProgress'
	| 'openActivityLog'
	| 'toggleFavorite'
	| 'delete'
	| 'openGoalPermissions'
	| TeamAction;
export type Option = PopupListItem<Action>;

export default class GoalsItemBaseComponent<T extends Args> extends Component<T> {
	@service('popup') declare popupService: Service.Popup;
	@service('request') declare requestService: Service.Request;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('account') declare accountService: Service.Account;
	@service('model-manager/activity-logs')
	declare mmActivityLogsService: Service.ModelManager.ActivityLogs;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('store') declare storeService: Service.Store;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('team') declare teamService: Service.Team;

	@tracked isVisibleOnScreen = false;

	get emblemTypeName(): string | null {
		const model = this.args.model;

		if (model.model.newBadge) {
			return EmblemType.NEW;
		}

		return null;
	}

	get model() {
		return this.args.model;
	}

	get goalData() {
		// will be always available bcos goal is saved
		return this.model.primaryDataModel!;
	}

	get currentValueStr() {
		const goalData = this.goalData;
		if (!goalData || goalData.isLoadingData) {
			return '';
		}

		return goalData.currentValueStr || '-';
	}

	get isDemoAccount(): boolean {
		return this.accountService.isDemoAccount;
	}

	get showDragHandle(): boolean {
		if (this.isDemoAccount || this.args.hideDragHandle) {
			return false;
		}

		return this.model.isOwnedByMe;
	}

	get goalRenamedTrackingObj() {
		return { event: TrackingEvent.GOAL_RENAME, data: { goal_id: this.model.id } };
	}

	get dataPerformanceSummaryObject() {
		return {
			objectType: DataPerformanceSummaryType.GOAL,
			objectId: this.model.id || '',
		};
	}

	get checkVisibility(): boolean {
		return !!this.args.checkVisibility;
	}

	get options(): Option[] {
		const options: Option[] = [];
		// isTeamSectionItem = this.isTeamSectionItem;
		const model = this.model;

		if (model.canEdit && model.isManualGoal) {
			options.push({
				icon: 'Pencil',
				text: 'Update status',
				action: 'updateProgress',
			});
		}

		options.push({
			icon: 'EyeOpen',
			text: 'View',
			action: 'viewDetails',
		});

		if (model.canEdit) {
			if (!model.isManualGoal) {
				options.push({
					icon: 'Pencil',
					text: 'Update status',
					action: 'updateProgress',
				});
			}

			options.push({
				icon: 'Metrics',
				text: 'Progress settings',
				action: 'edit',
			});
		}

		options.push({
			class: model.favorite ? 'accent' : '',
			icon: model.favorite ? 'StarFill' : 'Star',
			text: this.model.favoriteText,
			action: 'toggleFavorite',
		});

		if (this.currentUserService.isAdmin) {
			options.push(this.mmActivityLogsService.getObjectForOptionsPopup());
		}

		if (this.model.canEditPermissions && this.model.isGoalType) {
			options.push({
				icon: 'UserAccess',
				text: 'User access',
				action: 'openGoalPermissions',
			});
		}

		if (model.canDelete) {
			options.push({
				class: 'delete',
				text: 'Delete',
				action: 'delete',
				icon: 'Trash',
			});
		}

		options.push(...this.teamService.getTeamSectionItemOptions(this.isTeamSectionItem));

		return options;
	}

	get isSelected() {
		return !!this.args.batchSelect?.isItemSelected(this.model);
	}

	get isTeamSectionItem(): boolean {
		return !!this.args.teamSectionItem && !!this.args.removeFromSection;
	}

	get canEdit(): boolean {
		const canEdit = this.args.model.canEdit,
			teamSectionItem = this.args.teamSectionItem;

		return teamSectionItem ? teamSectionItem.isCurrentUserMember && canEdit : canEdit;
	}

	get showActions(): boolean {
		const teamSectionItem = this.args.teamSectionItem;
		return teamSectionItem ? teamSectionItem.isCurrentUserMember : true;
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	viewDetails(): void {
		this.mmGoalsService.openGoalInsightsPage(this.model);
	}

	@action
	edit(): void {
		this.mmGoalsService.openEditPopup(this.args.model, 'goals');
	}

	@action
	updateProgress(): void {
		this.mmGoalsService.openUpdateProgressPopup(this.model);
	}

	@action
	toggleFavorite(): void {
		void this.mmGoalsService.toggleGoalFavorite(this.model);
	}

	@action
	openActivityLog() {
		this.mmActivityLogsService.createAndOpenPopup({
			model: this.model,
		});
	}

	@action
	openGoalPermissions() {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.analyticsService.event(TrackingEvent.GOAL_PERMISSIONS_POPUP, {
			goal_id: this.model.id,
		});

		this.mmGoalsService.openGoalAccessTypePopup(this.model);
	}

	@action
	delete(): void {
		this.args.batchSelect?.unselectAll();
		this.mmGoalsService.deleteGoal(this.args.model);
	}

	@action
	selectItem(selected: boolean): void {
		const { model, batchSelect } = this.args;
		batchSelect?.changeSelection(model, selected);
	}

	@action
	removeFromSection(): void {
		const teamSectionItem = this.args.teamSectionItem;

		if (teamSectionItem) {
			this.args.removeFromSection?.(teamSectionItem);
		}
	}

	@action
	visibleChanged(isVisible: boolean): void {
		this.isVisibleOnScreen = isVisible;
	}
}
